import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Video } from 'lucide-react';
import { AdvisorAvatar } from './AdvisorAvatar';

const defaultAdvisors = [
  {
    name: 'Beatrice',
    title: 'The Strategist',
    description: 'Strategic thinking and decision-making',
  },
  {
    name: 'Samuel',
    title: 'The Mentor',
    description: 'Personal growth and leadership',
  },
  {
    name: 'Leo',
    title: 'The Innovator',
    description: 'Creative solutions and innovation',
  },
  {
    name: 'Marcus',
    title: 'The Stoic',
    description: 'Philosophy and mindset',
  }
];

export const AdvisorsSection: React.FC = () => {
  return (
    <div className="px-4 py-12 sm:py-16 md:py-24 bg-gradient-to-r from-black/40 to-black/60 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
            Meet Your Board of Advisors
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-6 sm:mb-8 px-2">
            Each advisor brings unique expertise and thinking styles. Mix and match for group discussions, 
            or go deep with 1:1 video sessions.
          </p>
          
          {/* Interaction Types */}
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-12">
            <div className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-green-500/20 border border-green-500/40">
              <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
              <span className="text-green-200 text-xs sm:text-sm font-medium">Group Chat & Voice</span>
            </div>
            <div className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/40">
              <Video className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
              <span className="text-blue-200 text-xs sm:text-sm font-medium">1:1 Video Calls</span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {defaultAdvisors.map((advisor, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm rounded-3xl p-4 sm:p-6 border border-white/10 hover:bg-white/10 transition-all duration-300 text-center"
            >
              <AdvisorAvatar name={advisor.name} size="large" className="mx-auto mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{advisor.name}</h3>
              <p className="text-orange-400 font-semibold mb-2 sm:mb-3 text-sm sm:text-base">{advisor.title}</p>
              <p className="text-white/70 text-xs sm:text-sm">{advisor.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Plus Custom Advisors */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-8 sm:mt-12"
        >
          <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-gradient-to-r from-purple-500/20 to-orange-500/20 border border-purple-400/30">
            <span className="text-white font-medium text-sm sm:text-base">+ Create custom advisors tailored to your needs</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};